/**
 * Calendar tool executor hook
 *
 * Executes calendar tools by interacting with the calendar UI store
 */

import { DateTime } from "luxon";
import { useCallback } from "react";
import { useNewCalendarUIStore } from "@/domains/appointment/new-calendar/store/use-new-calendar-ui-store";
import type { NewCalendarViewMode } from "@/domains/appointment/new-calendar/types";
import { useViewContextStore } from "../store/view-context-store";

type ToolExecutionResult =
  | {
      error: string;
    }
  | {
      message?: string;
      data?: unknown;
    };

/**
 * Parse relative date strings like "tomorrow", "next monday", "today"
 */
function parseRelativeDate(dateStr: string): DateTime | null {
  const lowerStr = dateStr.toLowerCase().trim();
  const now = DateTime.now();

  // Handle "today", "tomorrow", "yesterday"
  if (lowerStr === "today") return now;
  if (lowerStr === "tomorrow") return now.plus({ days: 1 });
  if (lowerStr === "yesterday") return now.minus({ days: 1 });

  // Handle "next week", "next month"
  if (lowerStr === "next week") return now.plus({ weeks: 1 });
  if (lowerStr === "next month") return now.plus({ months: 1 });
  if (lowerStr === "last week") return now.minus({ weeks: 1 });
  if (lowerStr === "last month") return now.minus({ months: 1 });

  // Handle "next [weekday]" or "last [weekday]"
  const nextWeekdayMatch = lowerStr.match(
    /^next\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)$/
  );
  if (nextWeekdayMatch) {
    const targetWeekday = nextWeekdayMatch[1];
    const weekdayMap: Record<string, number> = {
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sunday: 7,
    };
    const targetDay = weekdayMap[targetWeekday];
    const currentDay = now.weekday;
    const daysToAdd = targetDay <= currentDay ? 7 - currentDay + targetDay : targetDay - currentDay;
    return now.plus({ days: daysToAdd });
  }

  // Try parsing as ISO date
  const isoDate = DateTime.fromISO(dateStr);
  if (isoDate.isValid) return isoDate;

  // Try parsing as common formats
  const formats = ["yyyy-MM-dd", "dd/MM/yyyy", "MM/dd/yyyy"];
  for (const format of formats) {
    const parsed = DateTime.fromFormat(dateStr, format);
    if (parsed.isValid) return parsed;
  }

  return null;
}

export function useCalendarToolExecutor() {
  const setCurrentDate = useNewCalendarUIStore((state) => state.setCurrentDate);
  const setViewMode = useNewCalendarUIStore((state) => state.setViewMode);
  const currentDate = useNewCalendarUIStore((state) => state.currentDate);
  const _viewMode = useNewCalendarUIStore((state) => state.viewMode);

  /**
   * Execute updateCalendarView tool (unified navigation + view change)
   */
  const executeUpdateCalendarView = useCallback(
    (args: { date?: string; viewMode?: string }): ToolExecutionResult => {
      try {
        // Validate that at least one parameter is provided
        if (!args.date && !args.viewMode) {
          return {
            error: "At least one parameter (date or viewMode) must be provided",
          };
        }

        const validViewModes = ["list", "daily", "worker", "3-day", "week", "month"];
        let parsedDate: DateTime | null = null;
        const actions: string[] = [];

        // Handle date navigation if provided
        if (args.date) {
          parsedDate = parseRelativeDate(args.date);
          if (!parsedDate) {
            return {
              error: `Could not parse date: ${args.date}. Use ISO format (YYYY-MM-DD) or relative dates like 'tomorrow', 'next monday'.`,
            };
          }
          setCurrentDate(parsedDate);
          actions.push(`navigated to ${parsedDate.toFormat("yyyy-MM-dd")}`);
        }

        // Handle view mode change if provided
        if (args.viewMode) {
          if (!validViewModes.includes(args.viewMode)) {
            return {
              error: `Invalid view mode: ${args.viewMode}. Valid options: ${validViewModes.join(", ")}`,
            };
          }
          setViewMode(args.viewMode as NewCalendarViewMode);
          actions.push(`switched to ${args.viewMode} view`);
        }

        const currentViewMode = useNewCalendarUIStore.getState().viewMode;
        const currentDateStr =
          parsedDate?.toFormat("yyyy-MM-dd") || currentDate.toFormat("yyyy-MM-dd");

        return {
          message: `Successfully ${actions.join(" and ")}`,
          data: {
            date: currentDateStr,
            viewMode: args.viewMode || currentViewMode,
          },
        };
      } catch (error) {
        return {
          error: error instanceof Error ? error.message : "Failed to update calendar view",
        };
      }
    },
    [setCurrentDate, setViewMode, currentDate]
  );

  /**
   * Execute getDetailedViewContext tool
   */
  const executeGetDetailedContext = useCallback((): ToolExecutionResult => {
    try {
      const allLongContexts = useViewContextStore.getState().getAllLongContexts();

      return {
        message: "Retrieved all detailed view contexts",
        data: allLongContexts,
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : "Failed to get detailed context",
      };
    }
  }, []);

  /**
   * Main execution function that routes to the appropriate tool
   */
  const executeTool = useCallback(
    (toolName: string, args: Record<string, unknown>): ToolExecutionResult => {
      switch (toolName) {
        case "updateCalendarView":
          return executeUpdateCalendarView(args as { date?: string; viewMode?: string });
        case "getDetailedViewContext":
          return executeGetDetailedContext();
        default:
          return {
            error: `Unknown tool: ${toolName}`,
          };
      }
    },
    [executeUpdateCalendarView, executeGetDetailedContext]
  );

  return { executeTool };
}
